package com.macrofocus.eflux.network

import com.macrofocus.eflux.network.logical.LogicalBusId
import com.macrofocus.eflux.ui.PaletteType
import korlibs.time.DateTime
import popper.core.Placement
import web.geometry.DOMPoint
import web.svg.SVGGraphicsElement
import kotlin.properties.Delegates

data class EFluxNode(override val bus: CombinedBuses?, val point: Point, val dateTime: DateTime) : Node {
    override val id: LogicalBusId by point::id
    override val fullName: String = bus?.fullName ?: point.id.name

    val elements: List<SVGGraphicsElement> by point::elements

    var paletteType: PaletteType by Delegates.observable(PaletteType.dark) { _, _, _ ->
        updateFill()
    }

    var inactive: Boolean by Delegates.observable(false) { _, _, _ ->
        point.available = false
    }

    var probed: Boolean by Delegates.observable(false) { _, _, _ ->
        point.probed = probed
    }

    var selected: Boolean by Delegates.observable(false) { _, _, newValue ->
        point.selected = selected
        if(!newValue) {
            // Reset position
            clientOffsetX = 0.0
            clientOffsetY = 0.0
        }
    }

    var fillColor: String? by Delegates.observable(null) { _, _, _ ->  updateFill() }

    var size: NodeField<*>? by Delegates.observable(null) { _, _, _ ->  updateSize() }
    var color: NodeField<*>? by Delegates.observable(null) { _, _, _ ->  updateFill() }

    val boundingClientRect : DOMPoint
        get() {
            val boundingClientRect = elements.first().getBoundingClientRect()
            val x = boundingClientRect.x + (boundingClientRect.width / 2.0)
            val y = boundingClientRect.y + (boundingClientRect.height / 2.0)
            return DOMPoint(x, y)
        }

    val centerSVG : DOMPoint
        get() {
            val bbox = elements.first().getBBox()
            val x = bbox.x + (bbox.width / 2.0)
            val y = bbox.y + (bbox.height / 2.0)
            return DOMPoint(x, y)
        }

    val anchorSVG : DOMPoint
        get() {
            val bbox = elements.first().getBBox()
            val x = bbox.x + (bbox.width / 2.0)
            val y = bbox.y
            return DOMPoint(x, y)
        }
    var anchorEl = elements.first()
//    val anchorEl: Any
//        get() {
//            return object {
//                val clientWidth = elements.first().getBoundingClientRect().width
//                val clientHeight = elements.first().getBoundingClientRect().height
//                val getBoundingClientRect = {
//                    val boundingClientRect = elements.first().getBoundingClientRect()
//                    val x = boundingClientRect.x
//                    val y = boundingClientRect.y
//                    val width = boundingClientRect.width
//                    val height = boundingClientRect.height
//                    DOMRect(x, y, width, height)
//                }
//            }
//        }
//    val anchorEl: Any
//        get() {
//            return object {
//                val clientWidth = elements.first().getBoundingClientRect().width
//                val clientHeight = elements.first().getBoundingClientRect().height
//                val getBoundingClientRect = {
//                    val boundingClientRect = elements.first().getBoundingClientRect()
//                    val x = boundingClientRect.x
//                    val y = boundingClientRect.y
//                    val width = boundingClientRect.width
//                    val height = boundingClientRect.height
//                    DOMRect(x, y, width, height)
//                }
//            }
//        }

    /**
     * The top level SVG element. With SvgPanZoom, there are two nested SVG elements.
     */
    val ownerSVGElement = anchorEl.ownerSVGElement!!.ownerSVGElement!!

    /**
     * The SVG coordinates that can be used together with SvgPanZoom
     */
    val svgPanZoomCenter : DOMPoint
        get() {
            val svg = ownerSVGElement
            val svgBounds = svg.getBoundingClientRect()
            val bounds = boundingClientRect
            val point = svg.createSVGPoint()
            point.x = bounds.x - svgBounds.x
            point.y = bounds.y - svgBounds.y
            return point
        }

    /**
     * The screen coordinates that can be used together with SvgPanZoom panning. Relative to 0,0 the center of screen
     */
    val screenPanZoomCenter : DOMPoint
        get() {
            val svg = anchorEl.ownerSVGElement!!.ownerSVGElement!!
            val svgBounds = svg.getBoundingClientRect()
            val svgPoint = svg.createSVGPoint()
//            svgPoint.x = (svgBounds.width / 2.0)
//            svgPoint.y = (svgBounds.height / 2.0)
            svgPoint.x = svgBounds.x + (svgBounds.width / 2.0)
            svgPoint.y = svgBounds.y + (svgBounds.height / 2.0)
            val ctm = svg.getScreenCTM()!!
//            val newCtm = ctm.translate(-svg.getBBox().x,-svg.getBBox().y)
//            val newCtm = ctm.translate(0.0,0.0)
            val newCtm = ctm.translate(-svgBounds.x,-svgBounds.y)

            val svgScreen = svgPoint.matrixTransform(newCtm)

            val bounds = boundingClientRect
            val point = svg.createSVGPoint()
            point.x = bounds.x
            point.y = bounds.y
            val screen = point.matrixTransform(newCtm)
//            val screen = point.matrixTransform(anchorEl.ownerSVGElement!!.getScreenCTM()!!)
//            val screen2 = point.matrixTransform(ownerSVGElement.getScreenCTM()!!)
//            println("Screen: ${screen.x},${screen.y}")
//            println("Screen2: ${screen2.x},${screen2.y}")
//            return screen
            val point2 = svg.createSVGPoint()
            point2.x = (svgScreen.x) - screen.x
            point2.y = (svgScreen.y) - screen.y
            return point2

//            return centerSVG
        }

    val placement = Placement.top
    var clientOffsetX: Double = 0.0
        set(value) {
            field = value
            schemaX = screenToSVG(svgToScreen(anchorSVG).x + clientOffsetX, clientOffsetY).x
        }
    var clientOffsetY: Double = 0.0
        set(value) {
            field = value
            schemaY = screenToSVG(clientOffsetX, svgToScreen(anchorSVG).y + clientOffsetY).y
        }
    var schemaX: Double = screenToSVG(svgToScreen(anchorSVG).x + clientOffsetX, clientOffsetY).x
    var schemaY: Double =  screenToSVG(clientOffsetX, svgToScreen(anchorSVG).y + clientOffsetY).y

    val screenX: Double
        get() {
            return svgToScreen(schemaX, schemaY).x - svgToScreen(anchorSVG).x
        }
    val screenY: Double
        get() {
            return svgToScreen(schemaX, schemaY).y - svgToScreen(anchorSVG).y
        }

    fun screenToSVG(screenX : Double, screenY : Double) : DOMPoint {
        val p = DOMPoint(screenX, screenY)
        return screenToSVG(p)
    }

    fun screenToSVG(p : DOMPoint) : DOMPoint {
        return p.matrixTransform(ctm.inverse())
    }

    fun svgToScreen(svgX : Double, svgY : Double) : DOMPoint {
        val p = DOMPoint(svgX, svgY)
        return svgToScreen(p)
    }

    fun svgToScreen(p : DOMPoint) : DOMPoint {
        return p.matrixTransform(ctm)
    }

    val ctm
        get() = parentElement.getScreenCTM()!!

    val parentElement
//        get() = elements.first().ownerSVGElement!!
        get() = elements.first().parentElement as SVGGraphicsElement


    fun updateFill() {
        val color = color?.getColor(this, paletteType)
        if(color != null) {
            point.fillColor = color
        } else {
            point.fillColor = fillColor
        }
    }

    fun updateSize() {
        val size = size
        if(size != null) {
            val normalized = size.getNormalized(this)
            if(normalized != null) {
                point.scale = 0.5 + (normalized * 1.0)
            } else {
                point.scale = null
            }
        } else {
            // Set the default size
            point.scale = null
        }
    }
}